// Agency: Costs & usage — ch.06: "Internal usage view by office … Minimal
// metering from model/OCR/job/connector usage and configurable cost rates or
// supplier reconciliation. Mark estimates and period clearly; access restricted
// to agency operators."
//
// And acceptance V06: "Usage cost totals are labelled with the period and
// actual/estimated basis; no sensitive invoice content enters metering logs."
//
// ch.06 also says what this screen is NOT: "It is an internal operational view,
// not a customer ROI promise or an invoicing/payment module. Start with a simple
// table and aggregated metering; retain the selected layout without building a
// billing engine." The customer Results tab was the one thing the owner removed
// from the v3 layout, and a screen full of euro figures per office is the
// shortest path to putting it back. So this is a table of quantities, and a
// cost column that is empty unless somebody configured a price.
//
// ---------------------------------------------------------------------------
// Three numbers that render alike and mean different things
// ---------------------------------------------------------------------------
//
// The endpoint is careful about these and the screen has to be too, or the care
// is thrown away at the last step:
//
//   * a quantity of 0 — measured, and nothing happened;
//   * a quantity of null with basis `not_collected` — nothing measures this,
//     and no amount of work will change the figure. OCR pages, today;
//   * a cost of null — nobody has said what a unit costs. Not "free".
//
// A zero in any of the last two places would be a measurement this screen
// invented, and the third one would be a price.
//
// The tokens carry a fourth: `unmeteredCalls`, the model calls whose provider
// reported no usage at all. A small token total with twelve silent calls behind
// it is not a cheap month, and the figure is shown next to the total rather
// than in a footnote.

import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { request, tenantPath } from '../api/client.ts';
import type { Membership, UsageLine, UsageReport } from '../api/types.ts';
import { agencyMemberships, useOfficeReads, type OfficeResult } from '../agency.ts';
import { useWorkspace } from '../state/workspace.tsx';

const LINE_LABELS: Readonly<Record<string, string>> = {
  model_call: 'Model calls',
  model_input_token: 'Model input tokens',
  model_output_token: 'Model output tokens',
  ocr_page: 'OCR pages',
  job: 'Jobs',
  job_attempt: 'Job attempts',
  connector_operation: 'Connector operations',
  stored_object: 'Stored documents',
  stored_byte: 'Stored bytes',
};

function label(key: string): string {
  return LINE_LABELS[key] ?? key.replace(/_/g, ' ');
}

/** The half-open window for a whole calendar month, in UTC. */
function monthWindow(offset: number, now: Date): { from: string; to: string; label: string } {
  const from = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - offset, 1));
  const to = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - offset + 1, 1));
  return {
    from: from.toISOString(),
    to: to.toISOString(),
    label: from.toLocaleDateString('nl-NL', { month: 'long', year: 'numeric', timeZone: 'UTC' }),
  };
}

export function AgencyUsageScreen(): ReactNode {
  const workspace = useWorkspace();
  const { token } = workspace;
  const offices = agencyMemberships(workspace.me);

  // Three choices, all of them a period somebody can name. "This month" is the
  // endpoint's own default and is asked for explicitly so the screen and the
  // server cannot disagree about where the month starts.
  const periods = useMemo(() => {
    const now = new Date();
    return [
      { key: 'this', ...monthWindow(0, now) },
      { key: 'last', ...monthWindow(1, now) },
      { key: 'previous', ...monthWindow(2, now) },
    ];
  }, []);
  const [periodKey, setPeriodKey] = useState('this');
  const period = periods.find((entry) => entry.key === periodKey) ?? periods[0];

  const from = period?.from ?? '';
  const to = period?.to ?? '';

  const read = useCallback(
    async (membership: Membership): Promise<UsageReport> =>
      request<UsageReport>(
        tenantPath(
          membership.tenantId,
          `/usage?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
        ),
        { token },
      ),
    [token, from, to],
  );

  const { results, busy, reload } = useOfficeReads(offices, read);

  if (offices.length === 0) {
    return (
      <>
        <div className="heading">
          <h1>Costs &amp; usage</h1>
          <p>This account operates no office.</p>
        </div>
        <div className="card">
          <div className="empty">
            <h3>No portfolio</h3>
            <p>
              ch.06 restricts this view to agency operators. This account holds no agency operator
              membership, so there is nothing it may meter.
            </p>
          </div>
        </div>
      </>
    );
  }

  const answered = (results ?? []).filter(
    (entry): entry is OfficeResult<UsageReport> & { value: UsageReport } => entry.value !== null,
  );
  // The basis is the SERVER's, and every office's server is the same one — but
  // it is read from an answer rather than assumed, so a deployment that turns
  // rates on mid-session is described correctly.
  const basis = answered[0]?.value.cost.basis ?? null;
  const currency = answered.find((entry) => entry.value.cost.currency !== null)?.value.cost
    .currency ?? null;

  return (
    <>
      <div className="heading">
        <h1>Costs &amp; usage</h1>
        <p>
          What was metered in each office you operate. An internal operational view — not an
          invoice, not a customer figure, and not a billing engine.
        </p>
      </div>

      <div className="toolbar">
        <div className="tabs" role="group" aria-label="Period">
          {periods.map((entry) => (
            <button
              key={entry.key}
              type="button"
              className={entry.key === periodKey ? 'on' : ''}
              onClick={() => setPeriodKey(entry.key)}
            >
              {entry.label}
            </button>
          ))}
        </div>
        <button type="button" className="btn" disabled={busy} onClick={reload}>
          {busy ? 'Reading…' : 'Refresh'}
        </button>
      </div>

      {/* V06: the period and the basis, on the page, above the numbers. */}
      <p className="callout" data-testid="period-and-basis">
        <b>{period?.label ?? ''}</b> — {period === undefined ? '' : formatWindow(period.from, period.to)}.{' '}
        {basis === null
          ? 'No office has answered yet.'
          : basis === 'no_rates_configured'
            ? 'No cost rates are configured in this deployment, so no cost is shown. The quantities below are measured; the money is not missing, it was never quoted.'
            : `Costs are ESTIMATES at the rates this deployment is configured with${currency === null ? '' : ` (${currency})`}. Nothing here is reconciled against a supplier invoice.`}
      </p>

      {results === null ? (
        <div className="card">
          <div className="empty">
            <h3>Reading your offices</h3>
            <p>One request per office, in turn.</p>
          </div>
        </div>
      ) : (
        results.map((entry) => (
          <OfficeUsage key={entry.membership.tenantId} entry={entry} />
        ))
      )}

      <div className="card section-space">
        <div className="card-head">
          <h2>How to read these numbers</h2>
        </div>
        <div className="card-body">
          <ul className="note-list">
            <li>
              <b>A blank quantity is not a zero.</b> A row marked <i>not collected</i> is one
              nothing in this system measures — OCR pages, today. No amount of work changes that
              figure, and a zero there would be a measurement nobody made.
            </li>
            <li>
              <b>Unmetered calls.</b> A model call whose provider reported no token usage is
              counted as a call and contributes nothing to the token totals. The count is shown
              beside them, because a small total with silent calls behind it is not a cheap month.
            </li>
            <li>
              <b>No cost is not free.</b> A blank cost means nobody configured a price for that
              unit. There is no default rate table in this build, because a default would be a
              price somebody eventually quotes to a customer on no authority.
            </li>
            <li>
              <b>Never "actual".</b> Actual would mean the provider's own invoice, matched to these
              counts. Nothing fetches one, so every cost here is an estimate and is labelled as one.
            </li>
            <li>
              <b>No invoice content is metered.</b> V06. These are counts and sums of integers; the
              endpoint's response schema declares nothing that could carry a supplier name or an
              amount.
            </li>
          </ul>
        </div>
      </div>
    </>
  );
}

function OfficeUsage({ entry }: { readonly entry: OfficeResult<UsageReport> }): ReactNode {
  const { membership, value, error } = entry;

  return (
    <div className="card section-space" data-testid={`usage-office-${membership.tenantId}`}>
      <div className="card-head">
        <div>
          <h2>{membership.tenantName}</h2>
          <p>
            {value === null
              ? 'Not read'
              : `${String(value.administrations.length)} administration${value.administrations.length === 1 ? '' : 's'} granted to you`}
          </p>
        </div>
        {value?.totals.cost === undefined || value.totals.cost === null ? null : (
          <div style={{ textAlign: 'right' }}>
            <b>{value.totals.cost}</b>
            <br />
            {/* Never the word "total cost" unqualified. */}
            <small>estimated, this period</small>
          </div>
        )}
      </div>

      {value === null ? (
        <div className="card-body">
          <p className="callout warn">{error}</p>
        </div>
      ) : value.administrations.length === 0 ? (
        <div className="empty">
          <h3>Nothing granted here</h3>
          <p>
            ch.02: agency support has no standing unrestricted access. This office has granted you
            no administration, so there is nothing of its usage to meter.
          </p>
        </div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Metered</th>
                <th>Unit</th>
                {value.administrations.map((administration) => (
                  <th key={administration.administrationId}>{administration.name}</th>
                ))}
                <th>Office</th>
                <th>Cost</th>
              </tr>
            </thead>
            <tbody>
              {value.totals.lines.map((total, index) => (
                <tr key={total.key} data-testid={`usage-line-${membership.tenantId}-${total.key}`}>
                  <td>
                    {label(total.key)}
                    <small>{total.source}</small>
                  </td>
                  <td>
                    <small>{total.unit}</small>
                  </td>
                  {value.administrations.map((administration) => (
                    <td key={administration.administrationId}>
                      <Quantity line={administration.lines[index]} />
                    </td>
                  ))}
                  <td>
                    <b>
                      <Quantity line={total} />
                    </b>
                  </td>
                  <td>
                    {total.cost === null ? (
                      // Not a zero, and not a dash that reads as one.
                      <span className="muted">
                        {total.rate === null ? 'No rate set' : '—'}
                      </span>
                    ) : (
                      <>
                        {total.cost}
                        <small>at {total.rate} per {total.unit}</small>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** One measured quantity, one seam, or one gap the caller should notice. */
function Quantity({ line }: { readonly line: UsageLine | undefined }): ReactNode {
  if (line === undefined) return <span className="muted">—</span>;
  if (line.basis === 'not_collected') {
    return <span className="badge warn">Not collected</span>;
  }
  return (
    <>
      {(line.quantity ?? 0).toLocaleString('nl-NL')}
      {line.unmeteredCalls !== null && line.unmeteredCalls > 0 ? (
        <small>{line.unmeteredCalls} call(s) reported no usage</small>
      ) : null}
    </>
  );
}

/** The exact window, so "this month" is checkable rather than trusted. */
function formatWindow(from: string, to: string): string {
  const start = new Date(from);
  const end = new Date(to);
  return `${start.toISOString().slice(0, 10)} up to but not including ${end.toISOString().slice(0, 10)}`;
}
